import { html, LitElement } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { forms } from '../styles/forms.style.js';
import { style } from './esign-input.style.js';
import '@cof/fs-checkbox-wc/fs-checkbox.js';
import { REFI_ESIGN_CONST } from '../../../constants/app.constants.js';

class EsignInput extends LitElement {
  constructor() {
    super();
    this.input = null;
  }

  static get properties() {
    return {
      errMessage: { type: Boolean },
      focus: { type: Boolean, attribute: true },
      name: { type: String, attribute: true },
      label: { type: String, attribute: true },
      type: { type: String, attribute: true },
      pattern: { type: Object, attribute: true },
      helper: { type: String, attribute: true },
      checkbox: { type: Object, attribute: true },
    };
  }

  static get styles() {
    return [forms, style];
  }

  render() {
    return html`
      <div class="form-field ${this.type === 'checkbox' ? '' : 'not-a-checkbox-mb'}">
        ${this.type === 'checkbox'
          ? html`
              <fs-checkbox
                type="${this.type}"
                id="${this.name.toCamelCase()}"
                class="grv-checkbox ${this.errMessage ? `error` : ''}"
                value="true"
                @blur="${this.checkValidity}"
                @change="${this.checkValidity}"
                @valid="${this.setErrMessage}"
                @invalid="${this.setErrMessage}"
                @click=${this.handleCheckboxClick}
                required
                >${this.label}</fs-checkbox
              >
            `
          : html`
              <label for="${this.label.toCamelCase()}" class="grv-label">${this.label}</label>
              <input
                type="${this.type}"
                .value="${this.value}"
                id="${this.label.toCamelCase()}"
                class="grv-input ${this.errMessage ? `error` : ''}"
                @blur="${this.checkValidity}"
                @input="${this.checkValidity}"
                @valid="${this.setErrMessage}"
                @invalid="${this.setErrMessage}"
                required
                pattern=${ifDefined(this.pattern?.rule)}
              />
            `}
        ${this.errMessage ? html` <p class="grv-helper error ${this.type === 'checkbox' ? 'grv-helper-checkbox' : ''}">${this.errMessage}</p> ` : ''} ${this.helper ? html` <p class="grv-helper">${this.helper}</p> ` : ''}
      </div>
    `;
  }

  firstUpdated() {
    if (this.type === 'checkbox') {
      this.input = this.shadowRoot.querySelector('fs-checkbox');
      this.checkbox = this.shadowRoot.querySelector('.grv-checkbox');
    } else {
      this.input = this.shadowRoot.querySelector('input');
    }
    const oldValidity = this.input.checkValidity;
    this.input.checkValidity = function checkValidity() {
      const isValid = oldValidity.apply(this);
      if (isValid) {
        this.dispatchEvent(new CustomEvent('valid'));
      }
      return isValid;
    };
    if (this.focus) {
      this.input.focus();
    }
  }

  setErrMessage(ev) {
    if (!this.input.validity.valid && typeof this.pattern?.message === 'function') {
      this.errMessage = this.pattern.message(this.input.validity, this.input.value);
      this.checkboxErrorState();
      return;
    }

    if (this.input.validity.patternMismatch) {
      this.errMessage = this.pattern.message;
      this.checkboxErrorState();
      return;
    }

    if (ev.target.validationMessage) {
      this.errMessage = this.type === 'checkbox' ? 'Please agree to the above conditions' : 'This field is required';
      this.checkboxErrorState();
      return;
    }

    this.errMessage = '';
  }

  checkValidity(ev) {
    ev.target.checkValidity();
  }

  checkboxErrorState() {
    if (this.type === 'checkbox' && this.checkbox != null) {
      this.checkbox.input.classList.add('fs-checkbox--error');
    }
  }

  handleCheckboxClick() {
    REFI_ESIGN_CONST.TRACK_EVENT(`consent ${this.name}`);
    const checkedStatusText = this.checkbox.input.checked ? 'checked' : 'unchecked';
    if (this.checkbox.input.classList != null && this.checkbox.input.classList.contains('fs-checkbox--error')) {
      this.checkbox.input.classList.remove('fs-checkbox--error');
    }
    if (this.name === 'agreeToEsign') {
      document.dispatchEvent(new CustomEvent('LSA checkbox clicked'));
    } else if (this.name === 'agreeToLender') {
      document.dispatchEvent(new CustomEvent('LH checkbox clicked'));
    } else {
      document.dispatchEvent(new CustomEvent('checkbox clicked'));
    }
    REFI_ESIGN_CONST.TRACK_EVENT(`consent ${checkedStatusText} ${this.name}`);
  }
}

customElements.define('esign-input', EsignInput);
